import type { BtnCheckboxMode } from '@shared/ui/buttons'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useToast } from '@shared/ui/toast'

import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleChangeTutorApi } from '../api/schedule-change-tutor.hook'
import { useLessonRecurringTrackApi } from '../api/lessons/recurring-track.hook'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'

interface ModalDisciplineTutorChangeConfirmReturn {
  refKidName: ComputedRef<string>
  refIsConfirmed: ComputedRef<boolean>
  refConfirmMode: ComputedRef<BtnCheckboxMode>
  refIsSaving: ComputedRef<boolean>
  toggleConfirmed: () => void
  close: () => void
  back: () => void
  confirm: () => Promise<void>
}

/** Assigns the picked tutor and replaces the schedule from now; the lessons are rebooked in the background. */
export const useModalDisciplineTutorChangeConfirm = (): ModalDisciplineTutorChangeConfirmReturn => {
  const toast = useToast()
  const store = useDisciplineScheduleStore()
  const { refKidName, refDisciplineId, customerNow, close, back, refreshDiscipline } = useDisciplineScheduleContext()

  const refIsConfirmed = computed<boolean>(() => store.isConfirmed)
  const refConfirmMode = computed<BtnCheckboxMode>(() => (store.hasConfirmError ? 'error' : 'normal'))
  const refIsSaving = computed<boolean>(() => store.isSaving)

  const toggleConfirmed = (): void => {
    store.setConfirmed(!store.isConfirmed)
  }

  const confirm = async (): Promise<void> => {
    if (store.isSaving) return

    const tutorId = store.getChosenTutorId
    const reason = store.getTutorReason
    const days = store.getChosenSlots

    if (!days.length || !tutorId || !reason || !store.isConfirmed) {
      if (!store.isConfirmed) store.setConfirmError(true)
      toast.warning({ title: 'Warning!', description: 'You need to fill all the required fields.' })
      return
    }

    store.setSaving(true)

    if (store.getChosenAvailability < 100) {
      void useLessonRecurringTrackApi({
        disciplineId: refDisciplineId.value,
        payload: {
          start_date: customerNow().format(DateFormat.DefaultDate),
          days,
          tutor_id: tutorId,
          availability: store.getChosenAvailability,
        },
      })
    }

    const isChanged = await useDisciplineScheduleChangeTutorApi({
      disciplineId: refDisciplineId.value,
      payload: {
        days,
        reason,
        new_tutor_id: tutorId,
        sub_reasons: store.getTutorSubReasons.length ? store.getTutorSubReasons : null,
        comment: store.getTutorComment.trim() || null,
      },
    })

    if (isChanged) await refreshDiscipline()

    store.setSaving(false)
    if (isChanged) store.closeFlow()
  }

  return {
    refKidName,
    refIsConfirmed,
    refConfirmMode,
    refIsSaving,
    toggleConfirmed,
    close,
    back,
    confirm,
  }
}
